import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { FaClipboardCheck, FaSearch, FaFilter, FaEye, FaEdit, FaStar, FaCalendarAlt, FaUser, FaCheckCircle, FaTimes, FaExternalLinkAlt } from 'react-icons/fa';
import axios from 'axios';
import { patientNationalId, patientDisplayName } from '../utils/patientRef';
import { Link } from 'react-router-dom';

const Reviews = () => {
  const { user, token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReview, setSelectedReview] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/reviews/supervisor`, config);
      setReviews(response.data || []);
    } catch (err) {
      setError('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const filteredReviews = reviews.filter(review => {
    const matchesSearch = review.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         review.patientId?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         review.procedureType?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || review.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleReviewClick = (review) => {
    setSelectedReview(review);
    setShowReviewModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? 'text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#0077B6]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Reviews</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Manage and review student submissions</p>
          </div>
          <div className="text-xs sm:text-sm text-gray-500">
            {reviews.length} total reviews
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search reviews..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6] text-sm"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6] text-sm"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="declined">Declined</option>
          </select>
        </div>
      </div>

      {/* Reviews Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredReviews.map((review, index) => (
          <motion.div
            key={review.id || review._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => handleReviewClick(review)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-[rgba(0,119,182,0.1)] rounded-lg">
                  <FaClipboardCheck className="h-5 w-5 text-[#0077B6]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{review.studentName || 'Unknown'}</h3>
                  <p className="text-sm text-gray-600">{review.procedureType}</p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(review.status)}`}>
                {review.status}
              </span>
            </div>
            
            <div className="space-y-2 mb-4">
              <div className="flex items-center text-sm text-gray-600">
                <FaUser className="h-4 w-4 mr-2" />
                <span className="truncate">{review.patientId?.fullName || 'No patient'}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <FaCalendarAlt className="h-4 w-4 mr-2" />
                <span>{new Date(review.submittedDate).toLocaleDateString()}</span>
              </div>
            </div>
            
            {review.procedureQuality > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                  <span>Quality Rating</span>
                  <span>{review.procedureQuality}/5</span>
                </div>
                {renderStars(review.procedureQuality)}
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>Submitted</span>
                <span>{new Date(review.submittedDate).toLocaleDateString()}</span>
              </div>
              <button className="p-2 text-gray-400 hover:text-[#0077B6] transition-colors">
                <FaEye className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredReviews.length === 0 && (
        <div className="text-center py-12">
          <FaClipboardCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews found</h3>
          <p className="text-gray-600">
            {searchTerm ? 'Try adjusting your search terms.' : 'No reviews are currently available.'}
          </p>
        </div>
      )}

      {/* Review Detail Modal */}
      {showReviewModal && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto mx-2 sm:mx-0"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Review Details</h2>
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="h-16 w-16 rounded-full bg-[rgba(0,119,182,0.1)] flex items-center justify-center">
                    <FaClipboardCheck className="h-8 w-8 text-[#0077B6]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{selectedReview.studentName}</h3>
                    <p className="text-gray-600">{selectedReview.procedureType}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Student Information</h4>
                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <FaUser className="h-4 w-4 mr-2" />
                        <span>{selectedReview.studentName}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Student ID:</span> {selectedReview.studentId || 'N/A'}
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Patient Information</h4>
                    <div className="space-y-2">
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Name:</span> {patientDisplayName(selectedReview.patientId, 'N/A')}
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">National ID:</span> {patientNationalId(selectedReview.patientId) || 'N/A'}
                      </div>
                      {patientNationalId(selectedReview.patientId) && (
                        <Link
                          to={`/supervisor/patient/${patientNationalId(selectedReview.patientId)}`}
                          className="inline-flex items-center gap-2 mt-1 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white text-xs font-medium hover:opacity-90"
                        >
                          <FaExternalLinkAlt className="h-3 w-3" />
                          Open the full patient record
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Procedure Details</h4>
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Type:</span> {selectedReview.procedureType}
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Submitted:</span> {new Date(selectedReview.submittedDate).toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Status:</span> 
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedReview.status)}`}>
                        {selectedReview.status}
                      </span>
                    </div>
                  </div>

                </div>
                
                {selectedReview.comment && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Student Comment</h4>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{selectedReview.comment}</p>
                  </div>
                )}
                
                {selectedReview.procedureQuality > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Quality Rating</h4>
                    <div className="flex items-center space-x-2">
                      {renderStars(selectedReview.procedureQuality)}
                      <span className="text-sm text-gray-600 ml-2">{selectedReview.procedureQuality}/5</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Reviews;
